(() => {
  const CONTAINER = '#ProductGridContainer';
  const CONFIG_ID = 'CollectionInfiniteAjaxConfig';
  const SENTINEL = '#CollectionInfiniteSentinel';
  const GRID = '#product-grid';

  function readConfig(container) {
    const el = container.querySelector(`#${CONFIG_ID}`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function buildSectionPageUrl(sectionId, page) {
    const out = new URLSearchParams();
    out.set('section_id', sectionId);

    const fromLoc = new URLSearchParams(window.location.search);
    fromLoc.delete('page');
    fromLoc.delete('section_id');
    for (const [k, v] of fromLoc.entries()) {
      out.append(k, v);
    }
    out.set('page', String(page));
    return `${window.location.pathname}?${out.toString()}`;
  }

  function cancelScrollTriggers(root) {
    root.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
    });
  }

  class CollectionInfiniteScroll {
    static observer = null;
    static loading = false;
    static ctx = null;

    static disconnect() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.ctx = null;
    }

    static setup() {
      this.disconnect();
      const container = document.querySelector(CONTAINER);
      if (!container) return;

      const cfg = readConfig(container);
      const sentinel = container.querySelector(SENTINEL);
      const grid = document.querySelector(GRID);
      if (!cfg || !sentinel || !grid) return;

      if (cfg.totalPages <= cfg.initialPage) return;

      const sectionId = grid.getAttribute('data-id');
      if (!sectionId) return;

      this.ctx = {
        cfg,
        nextPage: cfg.initialPage + 1,
        sentinel,
        grid,
        sectionId,
      };

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting || CollectionInfiniteScroll.loading) return;
          CollectionInfiniteScroll.loadNext();
        },
        { rootMargin: '400px 0px 0px 0px', threshold: 0 },
      );
      this.observer.observe(sentinel);
    }

    static async loadNext() {
      const c = this.ctx;
      if (!c || this.loading) return;
      const { cfg, nextPage, grid, sentinel, sectionId } = c;
      if (nextPage > cfg.totalPages) {
        this.disconnect();
        return;
      }

      const status = sentinel.querySelector('.collection-infinite-scroll__status');
      this.loading = true;
      if (status) status.hidden = false;

      try {
        const url = buildSectionPageUrl(sectionId, nextPage);
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetchedGrid = doc.querySelector('#product-grid');
        if (!fetchedGrid) throw new Error('Missing #product-grid in section response');

        const items = fetchedGrid.querySelectorAll(':scope > li.grid__item');
        if (items.length === 0) {
          c.nextPage += 1;
          if (c.nextPage > cfg.totalPages) {
            this.disconnect();
          }
          return;
        }

        items.forEach((li) => {
          const node = document.importNode(li, true);
          cancelScrollTriggers(node);
          grid.appendChild(node);
        });

        c.nextPage += 1;

        if (c.nextPage > cfg.totalPages) {
          this.disconnect();
          return;
        }

        requestAnimationFrame(() => {
          if (CollectionInfiniteScroll.loading || !CollectionInfiniteScroll.observer) return;
          if (!CollectionInfiniteScroll.ctx || CollectionInfiniteScroll.ctx.nextPage > cfg.totalPages) return;
          const rect = sentinel.getBoundingClientRect();
          if (rect.top <= window.innerHeight + 400) {
            CollectionInfiniteScroll.loadNext();
          }
        });
      } catch (e) {
        console.error('[CollectionInfiniteScroll] section fetch failed — try refreshing', e);
        this.disconnect();
      } finally {
        this.loading = false;
        if (status) status.hidden = true;
      }
    }
  }

  document.addEventListener('facet:product-grid-updated', () => CollectionInfiniteScroll.setup());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CollectionInfiniteScroll.setup());
  } else {
    CollectionInfiniteScroll.setup();
  }
})();
